import { useState, useRef, useEffect, useLayoutEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Separator } from "./ui/separator";
import { toast } from "./SimpleToast";
import {
  Copy,
  Download,
  Link2,
  Search,
  X,
  Plus,
  Monitor,
  Eye,
  Settings,
  Loader2
} from "lucide-react";
import type { Language } from "../i18n";
import { ShareService } from "../services/shareService";
import { useTranslation } from "react-i18next";

interface ShareDropdownProps {
  language: Language;
  theme: 'dark' | 'light';
  analysisTitle?: string;
  isOpen: boolean;
  onClose: () => void;
  triggerRef: React.RefObject<HTMLButtonElement>;
  shareUrl?: string | null;
  hasBeenShared?: boolean;
  onExportPDF?: () => Promise<void>;
  isExportingPDF?: boolean;
  shareError?: string | null;
  // Новые пропсы для работы с флагом public
  shareId?: string;
  externalId?: string;
  onPublicToggle?: (isPublic: boolean) => void;
}


export function ShareDropdown({ 
  language, 
  theme, 
  analysisTitle = "", 
  isOpen, 
  onClose, 
  triggerRef, 
  shareUrl, 
  hasBeenShared = false, 
  onExportPDF, 
  isExportingPDF = false, 
  shareError,
  shareId,
  externalId,
  onPublicToggle
}: ShareDropdownProps) {
  const [emailInput, setEmailInput] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [copiedLink, setCopiedLink] = useState('');
  const [isMounted, setIsMounted] = useState(false);
  const [isUpdatingPublic, setIsUpdatingPublic] = useState(false);
  const [dropdownRight, setDropdownRight] = useState<number | null>(null);
  const [dropdownLeft, setDropdownLeft] = useState<number | null>(null);
  const [arrowRight, setArrowRight] = useState<number | null>(null);
  
  const dropdownRef = useRef<HTMLDivElement>(null);
  const isDark = theme === 'dark';

  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Вычисляем позицию блока относительно кнопки "Поделиться"
  useLayoutEffect(() => {
    if (!isOpen || !triggerRef.current) {
      // Сбрасываем позицию при закрытии
      setDropdownRight(null);
      setDropdownLeft(null);
      setArrowRight(null);
      return;
    }

    const updatePosition = () => {
      const trigger = triggerRef.current;
      if (!trigger) return;

      const triggerRect = trigger.getBoundingClientRect();
      const windowWidth = window.innerWidth;
      
      // На десктопе выравниваем правый край блока (без тени) по правому краю кнопки
      // На мобильных оставляем фиксированное позиционирование
      if (window.innerWidth >= 1024) {
        // На десктопе dropdown имеет фиксированную ширину w-96 = 384px
        // Вычисляем left позицию так, чтобы правый край блока совпал с правым краем кнопки
        // left = triggerRect.right - 384px
        const dropdownWidth = 384; // w-96 в Tailwind
        const leftPosition = triggerRect.right - dropdownWidth;
        setDropdownLeft(leftPosition);
        setDropdownRight(null); // Отключаем right позиционирование
        
        // Вычисляем позицию стрелочки: она должна указывать на центр кнопки
        // Правый край блока совпадает с правым краем кнопки (triggerRect.right)
        // Центр кнопки находится на расстоянии triggerRect.width / 2 от правого края кнопки
        // Стрелочка имеет ширину 16px (w-4), поэтому её центр должен быть на triggerRect.width / 2 от правого края блока
        // Позиция правого края стрелочки = triggerRect.width / 2 - 8 (половина ширины стрелочки)
        const arrowPositionFromRight = triggerRect.width / 2 - 8;
        setArrowRight(arrowPositionFromRight);
      } else {
        // На мобильных используем фиксированное позиционирование
        setDropdownRight(null);
        setDropdownLeft(null);
        setArrowRight(null);
      }
    };

    // Вычисляем позицию синхронно перед отрисовкой
    updatePosition();
    
    // Создаем функции-обертки для обработчиков событий
    const handleResize = updatePosition;
    const handleScroll = updatePosition;
    
    // Обновляем позицию при изменении размера окна
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll, true);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen, triggerRef]);

  // Считаем, что по умолчанию public = false
  // Автоматическая загрузка статуса убрана для упрощения

  // Не обновляем copiedLink автоматически - только при нажатии кнопки "Копировать ссылку"

  const { t } = useTranslation('common');

  // Закрытие при клике вне формы
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose, triggerRef]);

  // Закрытие при нажатии Escape
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  const validateEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSendEmails = useCallback(() => {
    if (!isMounted) return;
    
    if (!emailInput.trim()) {
      toast.error(t('emptyEmails'));
      return;
    }

    const emailAddresses = emailInput
      .split(/[,;\n]/)
      .map(email => email.trim())
      .filter(email => email.length > 0);

    if (emailAddresses.length === 0) {
      toast.error(t('emptyEmails'));
      return;
    }

    const invalidEmails = emailAddresses.filter(email => !validateEmail(email));
    if (invalidEmails.length > 0) {
      toast.error(`${t('invalidEmail')}: ${invalidEmails.join(', ')}`);
      return;
    }

    // Получаем актуальную ссылку для отправки
    const urlToShare = shareUrl || `https://plusminus.ai/analysis/${encodeURIComponent(analysisTitle)}/share`;
    
    // Создаем тему письма
    const subject = language === 'ru' 
      ? `Анализ "${analysisTitle}" - Плюс Минус`
      : `Analysis "${analysisTitle}" - Plus Minus`;
    
    // Создаем текст письма
    const body = language === 'ru'
      ? `Привет!\n\nПоделился с вами результатами анализа "${analysisTitle}" на платформе Плюс Минус.\n\nСсылка для просмотра: ${urlToShare}\n\nС уважением`
      : `Hello!\n\nI'm sharing the analysis results for "${analysisTitle}" on Plus Minus platform.\n\nView link: ${urlToShare}\n\nBest regards`;
    
    // Создаем mailto ссылку
    const mailtoLink = `mailto:${emailAddresses.join(',')}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    
    // Открываем email-клиент
    window.open(mailtoLink, '_self');
    
    // Очищаем поле ввода
    setEmailInput('');
    
    // Показываем уведомление
    const message = language === 'ru' 
      ? `Email-клиент открыт для ${emailAddresses.length} ${emailAddresses.length === 1 ? 'адреса' : 'адресов'}`
      : `Email client opened for ${emailAddresses.length} ${emailAddresses.length === 1 ? 'address' : 'addresses'}`;
    
    toast.success(message);
  }, [emailInput, shareUrl, analysisTitle, language, t, isMounted]);

  const handleCopyLink = useCallback(() => {
    if (!isMounted) return;
    
    // Используем реальный shareUrl если он есть, иначе создаем заглушку
    const urlToCopy = shareUrl || `https://plusminus.ai/analysis/${encodeURIComponent(analysisTitle)}/share`;
    navigator.clipboard.writeText(urlToCopy);
    setCopiedLink(urlToCopy);
    toast.success(t('linkCopied'));
  }, [shareUrl, analysisTitle, t, isMounted]);

  const handleDownload = useCallback(async () => {
    if (!isMounted) return;
    
    if (onExportPDF) {
      try {
        await onExportPDF();
        toast.success(t('downloadStarted'));
      } catch (error) {
        console.error('Error exporting PDF:', error);
        toast.error(t('pdfExportError'));
      }
    } else {
      toast.success(t('downloadStarted'));
    }
  }, [onExportPDF, t, language, isMounted]);

  const handleTogglePublic = useCallback(async () => {
    if (!isMounted || !shareId || !externalId || isUpdatingPublic) return;
    
    setIsUpdatingPublic(true);
    
    try {
      const newPublicValue = !isPublic;
      await ShareService.updatePublicFlag(shareId, externalId, newPublicValue);
      
      setIsPublic(newPublicValue);
      toast.success(newPublicValue ? t('publicLinkCreated') : t('publicLinkRemoved'));
      
      // Уведомляем родительский компонент об изменении
      if (onPublicToggle) {
        onPublicToggle(newPublicValue);
      }
    } catch (error) {
      console.error('Error updating public flag:', error);
      const errorMessage = error instanceof Error ? error.message : 'Ошибка при изменении флага публичности';
      toast.error(errorMessage);
    } finally {
      setIsUpdatingPublic(false);
    }
  }, [isMounted, shareId, externalId, isPublic, isUpdatingPublic, t, onPublicToggle]);

  const handleOpenWebsite = useCallback(() => {
    if (!isMounted) return;
    
    const urlToOpen = shareUrl || `https://plusminus.ai/analysis/${encodeURIComponent(analysisTitle)}/share`;
    window.open(urlToOpen, '_blank', 'noopener,noreferrer');
  }, [shareUrl, analysisTitle, isMounted]);


  if (!isOpen) return null;

  const overlay = (
    <div 
      className="bg-black/10 z-[51]" 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100dvh',
        minHeight: '100vh',
        margin: 0,
        padding: 0,
        inset: 0
      }}
      onClick={onClose} 
    />
  );

  return (
    <>
      {/* Overlay для затемнения - рендерим через Portal напрямую в body */}
      {typeof document !== 'undefined' && createPortal(overlay, document.body)}
      
      {/* Выпадающая форма */}
      <div 
        ref={dropdownRef}
        style={
          dropdownLeft !== null 
            ? { left: `${dropdownLeft}px`, right: 'auto', transition: 'none !important', animation: 'none !important' } 
            : dropdownRight !== null 
            ? { right: `${dropdownRight}px`, left: 'auto', transition: 'none !important', animation: 'none !important' }
            : undefined
        }
        className={`fixed top-12 sm:top-16 lg:top-[73px] ${dropdownLeft === null && dropdownRight === null ? 'right-2 sm:right-4' : ''} w-[calc(100vw-1rem)] sm:w-96 max-w-[calc(100vw-1rem)] sm:max-w-[calc(100vw-2rem)] lg:max-w-none lg:w-96 bottom-auto lg:bottom-auto rounded-lg shadow-2xl border ${
          isDark 
            ? 'bg-slate-900 border-slate-700 text-white' 
            : 'bg-white border-gray-200'
        } z-[52]`}
      >
        {/* Стрелочка - скрываем на мобильных */}
        <div 
          style={arrowRight !== null ? { right: `${arrowRight}px` } : undefined}
          className={`hidden lg:block absolute -top-2 ${arrowRight === null ? 'right-8' : ''} w-4 h-4 transform rotate-45 border-t border-l ${
            isDark 
              ? 'bg-slate-900 border-slate-700' 
              : 'bg-white border-gray-200'
          }`} 
        />
        
        {/* Заголовок с информацией */}
        <div className={`px-4 py-3 border-b ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
          <div className="flex items-center justify-between">
            <div>
              <h3 className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {t('shareAnalysis')}
              </h3>
              <div className="flex items-center gap-1 mt-1">
                <Eye className={`w-4 h-4 ${isDark ? 'text-slate-400' : 'text-gray-500'}`} />
                <span className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                  1 {t.visitors}
                </span>
                <Settings className={`w-4 h-4 ml-2 ${isDark ? 'text-slate-400' : 'text-gray-500'}`} />
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className={`w-8 h-8 ${
                isDark 
                  ? 'hover:bg-slate-700 text-slate-400 hover:text-white' 
                  : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'
              }`}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Отображение ошибки */}
        {shareError && (
          <div className={`px-4 py-4 ${isDark ? 'bg-red-900/20 border-red-800/30' : 'bg-red-50 border-red-200'} border-l-4`}>
            <div className="flex items-start gap-3">
              <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                isDark ? 'bg-red-500/20 text-red-400' : 'bg-red-100 text-red-600'
              }`}>
                <X className="w-3 h-3" />
              </div>
              <div>
                <p className={`text-sm font-medium ${isDark ? 'text-red-300' : 'text-red-800'}`}>
                  {t('shareError')}
                </p>
                <p className={`text-xs mt-1 ${isDark ? 'text-red-400' : 'text-red-600'}`}>
                  {shareError}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Основное содержимое - показываем только если нет ошибки */}
        {!shareError && (
          <div className="p-4 space-y-4 max-h-[calc(100vh-200px)] overflow-y-auto">
          {/* Отправить ссылку по email */}
          <div className="space-y-3">
            <h4 className={`text-xs uppercase tracking-wide font-medium ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
              {t.sendLinkTitle}
            </h4>
            
            {/* Поле для ввода email адресов */}
            <div className="space-y-2">
              <div className="relative">
                <Search className={`absolute left-3 top-3 w-4 h-4 ${
                  isDark ? 'text-slate-400' : 'text-gray-400'
                }`} />
                <textarea
                  placeholder={t('emailPlaceholder')}
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  rows={3}
                  className={`w-full pl-10 pr-3 py-2 rounded-md border resize-none text-sm ${
                    isDark 
                      ? 'bg-slate-800 border-slate-600 text-white placeholder:text-slate-400' 
                      : 'bg-white border-gray-200 placeholder:text-gray-500'
                  }`}
                />
              </div>
              
              {/* Кнопка отправки */}
              <Button 
                onClick={handleSendEmails}
                disabled={!emailInput.trim()}
                className={`w-full ${
                  isDark
                    ? 'bg-blue-600 hover:bg-blue-500 text-white'
                    : 'bg-blue-600 hover:bg-blue-500 text-white'
                }`}
              >
                <Plus className="w-4 h-4 mr-2" />
                {t.sendLink}
              </Button>
            </div>

          </div>

          {/* Большая кнопка копирования ссылки */}
          <Button
            onClick={handleCopyLink}
            className={`w-full h-10 ${
              isDark
                ? 'bg-blue-600 hover:bg-blue-500 text-white'
                : 'bg-blue-600 hover:bg-blue-500 text-white'
            }`}
          >
            <Copy className="w-4 h-4 mr-2" />
            {t('copyLink')}
          </Button>

          {/* Отображение скопированной ссылки */}
          {copiedLink && (
            <div className="space-y-2">
              <h5 className={`text-xs font-medium ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                {t('shareableLink')}
              </h5>
              <div className="relative">
                <input
                  type="text"
                  value={copiedLink}
                  onChange={(e) => setCopiedLink(e.target.value)}
                  className={`w-full px-3 py-2 pr-10 rounded-md border text-sm ${
                    isDark 
                      ? 'bg-slate-800 border-slate-600 text-white' 
                      : 'bg-white border-gray-200 text-gray-900'
                  }`}
                  placeholder={t('editLink')}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    if (!isMounted) return;
                    navigator.clipboard.writeText(copiedLink);
                    toast.success(t('linkCopied'));
                  }}
                  className={`absolute right-1 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0 ${
                    isDark 
                      ? 'hover:bg-slate-700 text-slate-400 hover:text-white' 
                      : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Copy className="w-3 h-3" />
                </Button>
              </div>
            </div>
          )}

          {/* Действия */}
          <div className="grid grid-cols-3 gap-3 pt-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDownload}
              disabled={isExportingPDF}
              className={`flex flex-col items-center gap-1 h-16 transition-all duration-200 ${
                isDark 
                  ? 'hover:bg-slate-700 hover:border-slate-600 text-slate-300 hover:text-white border border-transparent' 
                  : 'hover:bg-gray-100 hover:border-gray-200 text-gray-600 hover:text-gray-900 border border-transparent'
              } ${isExportingPDF ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {isExportingPDF ? (
                <div className="w-5 h-5 animate-spin rounded-full border-2 border-current border-t-transparent" />
              ) : (
                <Download className="w-5 h-5" />
              )}
              <span className="text-xs">
                {isExportingPDF 
                  ? (language === 'ru' ? 'Скачивание...' : 'Downloading...')
                  : t('download')
                }
              </span>
            </Button>

            <Button
              variant="ghost"
              size="sm"
              disabled
              className={`flex flex-col items-center gap-1 h-16 transition-all duration-200 relative ${
                isDark 
                  ? 'text-slate-500 cursor-not-allowed' 
                  : 'text-gray-400 cursor-not-allowed'
              }`}
            >
              <Monitor className="w-5 h-5" />
              <span className="text-xs">{t.presentation}</span>
              <Badge className={`absolute -top-1 -right-1 text-xs px-1 py-0 ${
                isDark 
                  ? 'bg-orange-500/20 text-orange-400 border-orange-500/30' 
                  : 'bg-orange-500/10 text-orange-600 border-orange-300'
              }`}>
                {t('comingSoon')}
              </Badge>
            </Button>

            <Button
              variant="ghost"
              size="sm"
              onClick={handleTogglePublic}
              disabled={!shareId || !externalId || isUpdatingPublic}
              className={`flex flex-col items-center gap-1 h-16 transition-all duration-200 relative ${
                isDark 
                  ? (isPublic
                      ? 'text-green-400 hover:text-green-300 hover:bg-green-500/10'
                      : 'text-slate-300 hover:text-white hover:bg-slate-700')
                  : (isPublic
                      ? 'text-green-600 hover:text-green-700 hover:bg-green-50'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50')
              } ${(!shareId || !externalId || isUpdatingPublic) ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {isUpdatingPublic ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Link2 className="w-5 h-5" />
              )}
              <span className="text-xs">
                {isUpdatingPublic 
                  ? (language === 'ru' ? 'Обновление...' : 'Updating...')
                  : (language === 'ru' ? 'Общедост.' : 'Public')
                }
              </span>
              {isPublic && (
                <Badge className={`absolute -top-1 -right-1 text-xs px-1 py-0 ${
                  isDark 
                    ? 'bg-green-500/20 text-green-400 border-green-500/30' 
                    : 'bg-green-500/10 text-green-600 border-green-300'
                }`}>
                  {language === 'ru' ? 'вкл' : 'on'}
                </Badge>
              )}
            </Button>
          </div>

          {/* Дополнительные действия */}
          <div className="grid grid-cols-2 gap-3 pt-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleOpenWebsite}
              className={`flex items-center gap-2 h-10 transition-all duration-200 ${
                isDark 
                  ? 'hover:bg-slate-700 hover:border-slate-600 text-slate-300 hover:text-white border border-transparent' 
                  : 'hover:bg-gray-100 hover:border-gray-200 text-gray-600 hover:text-gray-900 border border-transparent'
              }`}
            >
              <Monitor className="w-4 h-4" />
              <span className="text-xs">{t.website}</span>
            </Button>

            <Button
              variant="ghost"
              size="sm"
              disabled
              className={`flex items-center gap-2 h-10 transition-all duration-200 relative ${
                isDark 
                  ? 'text-slate-500 cursor-not-allowed' 
                  : 'text-gray-400 cursor-not-allowed'
              }`}
            >
              <div className="w-4 h-4 bg-orange-600 rounded flex items-center justify-center">
                <span className="text-white text-xs font-bold">P</span>
              </div>
              <span className="text-xs">{t('powerpoint')}</span>
              <Badge className={`absolute -top-1 -right-1 text-xs px-1 py-0 ${
                isDark 
                  ? 'bg-orange-500/20 text-orange-400 border-orange-500/30' 
                  : 'bg-orange-500/10 text-orange-600 border-orange-300'
              }`}>
                {t('comingSoon')}
              </Badge>
            </Button>
          </div>
        </div>
        )}
      </div>
    </>
  );
}
